import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  MessageCircle, 
  CheckCircle, 
  Clock, 
  Zap, 
  ChevronRight, 
  Star, 
  Award, 
  VideoIcon,
  Briefcase,
  DollarSign,
  Users,
  ArrowRight,
  MessageSquare,
  FileBox,
  Mail,
  BarChart,
  Shield,
  Target,
  TrendingUp,
  UserCheck,
  Check,
  Book,
  Coffee,
  Unlock,
  LineChart,
  LayoutDashboard,
  Lightbulb,
  Network,
  Rocket
} from 'lucide-react';
import SectionTransitionEffect from '../components/ui/SectionTransitionEffect';
import { Link } from 'react-router-dom';
import SiteMetadata from '../components/SEO/SiteMetadata';

const Mentorship: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0] }
    }
  };

  const wins = [
    {
      highlight: "Took Saturdays off within six weeks",
      detail: "shop kept grossing $40k/mo."
    },
    {
      highlight: "Added $11k monthly profit",
      detail: "by adjusting pricing tiers (no extra cars)."
    },
    {
      highlight: "Hired a front-desk lead",
      detail: "who now closes 70% of inbound quotes solo."
    },
    {
      highlight: "Cut production time by 28%",
      detail: "with staff-designed process improvements."
    },
    {
      highlight: "Raised prices 15%",
      detail: "without losing a single client."
    },
    {
      highlight: "Eliminated 12+ hours of admin work",
      detail: "through strategic automation."
    }
  ];

  const rhythmItems = [
    {
      title: "4 Private Zoom Sessions / month",
      description: "Real-time strategy, role-play tough conversations, map next moves.",
      icon: VideoIcon
    },
    {
      title: "Unlimited Messenger Access (Mon-Fri)",
      description: "Quick feedback so momentum never stalls.",
      icon: MessageSquare
    },
    {
      title: "Systems & Concepts on Demand",
      description: "SOP templates, pricing scripts, ad angles—only when it solves your current block.",
      icon: FileBox
    },
    {
      title: "Weekly Accountability Check-ins",
      description: "Short, focused calls to keep you on track and address any roadblocks.",
      icon: Check
    }
  ];

  const changes = [
    {
      title: "Decision clarity",
      description: "you'll know exactly what to tackle each week instead of firefighting whatever dings loudest.",
      icon: Target
    },
    {
      title: "Owner-optional days",
      description: "one full weekday off the floor, guilt-free.",
      icon: Calendar
    },
    {
      title: "Predictable paydays",
      description: "profit you can forecast, not hope for.",
      icon: DollarSign
    },
    {
      title: "A stronger crew",
      description: "staff who stick because they see a future, not just a paycheck.",
      icon: Users
    },
    {
      title: "Strategic growth",
      description: "scale your shop according to plan, not by accident or necessity.",
      icon: TrendingUp
    },
    {
      title: "Operational freedom",
      description: "systems that work whether you're present or not.",
      icon: Unlock
    }
  ];

  const programIncludes = [
    {
      title: "1-on-1 Strategy Sessions",
      description: "Personalized coaching focused on your specific business challenges and goals.",
      icon: UserCheck
    },
    {
      title: "Complete Business Systemization",
      description: "Transform your shop into a well-oiled machine that runs smoothly with or without you present.",
      icon: LayoutDashboard
    },
    {
      title: "Accelerated Implementation Framework",
      description: "Turn strategies into action with structured frameworks that deliver rapid results for your business.",
      icon: Rocket
    },
    {
      title: "SOPs & Systems Library",
      description: "Access our complete collection of proven systems, templates, and documentation for every aspect of your business.",
      icon: Book
    },
    {
      title: "Revenue Growth Blueprint",
      description: "Custom growth strategy based on your shop's data to increase profits without working more hours.",
      icon: LineChart
    }
  ];
  
  const testimonials = [
    {
      quote: "I was drowning in daily operations and felt stuck at a revenue ceiling. Within 90 days of working with Gabe, we implemented systems that let me cut back 15 hours a week while maintaining our volume. Now we're on track for our first $105k month!",
      business: "$105k/month operator",
      image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=100"
    },
    {
      quote: "My PPF shop was busy but inefficient. Gabe helped me restructure pricing, train a manager, and implement tracking systems. We've doubled our bottom line in 6 months, and I finally took a real vacation. Best investment I've made for my business.",
      business: "$60k/month PPF shop",
      image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100"
    },
    {
      quote: "Not gonna lie - I was skeptical about coaching. But my detail & coating biz was killing me with 70-hr weeks. DetailerOS mentorship gave me a roadmap to delegate, systemize, and escape the technician trap. Now doing $45k/mo with half the stress.",
      business: "$45k/month detail and coating shop",
      image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100"
    }
  ];

  return (
    <div className="pt-32 pb-20">
      {/* SEO Metadata */}
      <SiteMetadata 
        page="mentorship"
        title="DetailerOS Business Mentorship - Expert Coaching for Auto Detailers"
        description="Get expert mentorship to transform your detailing business. Learn proven strategies for automating operations, improving profitability, and scaling your auto detailing business."
        keywords={[
          "auto detailing business coaching", 
          "detailing business mentorship", 
          "auto detailer consultant", 
          "car detailing business growth", 
          "detailing shop management",
          "car detailing profitability",
          "auto detailing business automation"
        ]}
      />

      {/* Background gradient */}
      <motion.div 
        className="absolute rounded-full opacity-60 blur-3xl -z-10 w-[800px] h-[800px] top-[100px] left-[10%]"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.6, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(204, 255, 0, 0.3), rgba(204, 255, 0, 0) 70%)'
        }}
      />
      
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <SectionTransitionEffect>
          <div className="text-center max-w-4xl mx-auto mb-16">
            <motion.span 
              className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              DetailerOS Mentorship Program
            </motion.span>
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4">
              For Anyone Looking To Build A Detailing Business That Runs Without Them
            </h1>
            <h2 className="text-xl md:text-2xl mb-6 font-semibold">
              "Most Detailers & PPF Businesses discover that working FEWER hours leads to HIGHER profits and growth
              Without being trapped in daily operations after 6 months."
            </h2>
            <p className="text-lg mb-6">
              While also <span className="text-accent font-semibold">taking weekends off</span>, 
              <span className="text-accent font-semibold"> earning predictable income</span>, and
              <span className="text-accent font-semibold"> building a self-managing team</span>.
            </p>
          </div>
        </SectionTransitionEffect>
        
        {/* Who this is for */}
        <div className="mb-24 max-w-4xl mx-auto">
          <motion.div 
            className="glassmorphic rounded-xl p-8 md:p-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-3xl font-bold mb-6">Who this is for</h2>
            <p className="text-xl mb-8">Mid-market owners doing solid revenue but still:</p>
            
            <motion.ul 
              className="space-y-5"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                "Clocking out long after the last hood is polished",
                "Fielding every \"Hey boss, quick question…\" text",
                "Wondering why the bank balance feels smaller than the job calendar",
                "Constantly putting out fires instead of building systems",
                "Afraid to take a day off because everything might fall apart",
                "Hitting revenue ceilings despite working harder than ever"
              ].map((item, i) => (
                <motion.li 
                  key={i} 
                  className="flex items-start gap-4"
                  variants={itemVariants}
                >
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent mt-1 flex-shrink-0">
                    <CheckCircle size={18} />
                  </div>
                  <span className="text-lg">{item}</span>
                </motion.li>
              ))}
            </motion.ul>
            
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-xl text-white/90 italic">
                You already proved you can sell film, tint, coatings, and service. Now it's about building a business that rewards you every month without taxing every hour you breathe.
              </p>
            </div>
          </motion.div>
        </div>
        
        {/* Testimonials Section */}
        <div className="mb-24 max-w-5xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-10">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Success Stories
              </motion.span>
              <h2 className="text-3xl font-bold">Real Results from Shop Owners Like You</h2>
            </div>
          </SectionTransitionEffect>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="glassmorphic rounded-xl p-6 relative overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="flex items-center space-x-1 mb-4 text-accent">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star key={star} size={16} className="fill-accent" />
                  ))}
                </div>
                
                <p className="italic mb-6 text-white/90">"{testimonial.quote}"</p>
                
                <div className="mt-auto">
                  <p className="font-bold text-accent">{testimonial.business}</p>
                </div>
                
                <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-accent/10 blur-xl -z-10"></div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* What changes in 90 days */}
        <div className="mb-24 max-w-5xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                The Transformation
              </motion.span>
              <h2 className="text-3xl font-bold mb-2">What changes inside 90 days</h2>
              <p className="text-lg text-white/80 max-w-3xl mx-auto">
                Most shop owners see significant improvements within the first three months of working with us.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {changes.map((item, index) => (
              <motion.div
                key={index}
                className="glassmorphic rounded-xl p-6 border border-white/10 hover:border-accent/20 transition-colors relative overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 10px 30px -5px rgba(204, 255, 0, 0.2)",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                    <item.icon size={24} />
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-white/80">{item.description}</p>
                  </div>
                </div>
                
                {/* Background decoration */}
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-accent/5 rounded-full z-0"></div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* What's Included Section */}
        <div className="mb-24 max-w-5xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Program Details
              </motion.span>
              <h2 className="text-3xl font-bold mb-2">What's Included</h2>
              <p className="text-lg text-white/80 max-w-3xl mx-auto">
                A comprehensive mentorship program designed specifically for detailing business owners.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programIncludes.map((item, index) => (
              <motion.div
                key={index}
                className="glassmorphic rounded-xl p-6 border border-white/10 hover:border-accent/20 transition-colors relative overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 10px 30px -5px rgba(204, 255, 0, 0.2)",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                    <item.icon size={24} />
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-white/80">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* How we work together */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-10">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                The Process
              </motion.span>
              <h2 className="text-3xl font-bold mb-2">How we work together</h2>
              <p className="text-lg text-white/80 max-w-3xl mx-auto mb-8">
                A structured approach to transforming your business while respecting your time.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <div className="glassmorphic rounded-xl p-8 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-lg font-bold">Rhythm</th>
                    <th className="text-left py-4 px-4 text-lg font-bold">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {rhythmItems.map((item, index) => (
                    <motion.tr 
                      key={index}
                      className="border-b border-white/5 hover:bg-white/5"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <td className="py-5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                            <item.icon size={20} />
                          </div>
                          <span className="font-semibold">{item.title}</span>
                        </div>
                      </td>
                      <td className="py-5 px-4 text-white/80">{item.description}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-8 p-5 bg-white/5 rounded-lg border border-white/10">
              <p className="text-center">
                Everything lives inside one shared workspace so notes, recordings, and resources never vanish into email purgatory.
              </p>
            </div>
          </div>
        </div>
        
        {/* My promise */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="glassmorphic border-accent rounded-xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-3xl -z-10"></div>
              
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/3 flex justify-center">
                  <div className="relative">
                    <a href="https://dgstorage.b-cdn.net/Gabe-12.jpg" target="_blank" rel="noopener noreferrer">
                      <img 
                        src="https://dgstorage.b-cdn.net/Gabe-12.jpg"
                        alt="Gabe - DetailerOS Mentor" 
                        className="w-64 h-64 object-cover rounded-xl border-2 border-accent shadow-glow-sm"
                      />
                    </a>
                    <div className="absolute -bottom-3 -right-3 bg-background p-2 rounded-lg border border-accent">
                      <Award className="text-accent" size={32} />
                    </div>
                  </div>
                </div>
                
                <div className="md:w-2/3">
                  <h2 className="text-3xl font-bold mb-6">My Promise</h2>
                  
                  <p className="text-lg leading-relaxed mb-6">
                    I'm Gabe, and I built and still operate a seven-figure detailing shop <a href="https://totaldetailingpa.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Total Detailing</a> and a marketing agency, <a href="https://detailingrowth.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Detailing Growth</a> that powers dozens more. I'm in the trenches daily, testing the moves we'll discuss. You'll get straight-talk tactics, not recycled "guru" decks.
                  </p>
                  
                  <p className="text-lg leading-relaxed mb-6">
                    Every strategy I implement has been battle-tested in real detailing businesses across the country. If I recommend it, it's because I've seen it work repeatedly, not because it looks good in theory.
                  </p>
                  
                  <p className="text-lg leading-relaxed italic text-accent font-semibold">
                    "My goal isn't just to make your business better, it's to make your life better. Success means a shop that doesn't consume your every waking moment."
                  </p>
                </div>
              </div>
            </div>
          </SectionTransitionEffect>
        </div>
        
        {/* Wins others have logged */}
        <div className="mb-24 max-w-5xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Proven Results
              </motion.span>
              <h2 className="text-3xl font-bold mb-2">Wins others have logged</h2>
              <p className="text-lg text-white/80 max-w-3xl mx-auto">
                These are real outcomes from detailing business owners in our mentorship program.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wins.map((win, index) => (
              <motion.div
                key={index}
                className="glassmorphic rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent mx-auto mb-4">
                  <Star size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">{win.highlight}</h3>
                <p className="text-white/70">{win.detail}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <p className="text-lg italic text-white/80">
              Your wins will look different—but they'll be real, documentable, and life-changing.
            </p>
          </div>
        </div>

        {/* Video Section - Not sure if it's worth it */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-8">
              <motion.span
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Real Results
              </motion.span>
              <h2 className="text-3xl font-bold mb-4">Not sure if it's worth it?</h2>
              <p className="text-lg text-white/80 mb-6">
                Is it worth it if I said that if you've got the drive, I can even bring you to 6 figure like I did for Sean?
              </p>
            </div>

            <motion.div
              className="glassmorphic rounded-xl p-4 md:p-8 overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src="https://www.youtube.com/embed/h6nZeJrs1a0"
                  title="DetailerOS Success Story - Sean's Journey"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </motion.div>
          </SectionTransitionEffect>
        </div>

        {/* Pricing Section */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="glassmorphic rounded-xl p-8 md:p-12 border-accent/30 shadow-glow-sm relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-accent/10 rounded-full blur-3xl -z-10"></div>
              
              <div className="text-center mb-10">
                <motion.span 
                  className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  Investment
                </motion.span>
                <h2 className="text-3xl font-bold mb-4">DetailerOS Mentorship Program</h2>
                <p className="text-lg text-white/80 max-w-3xl mx-auto mb-6">
                  Serious transformation requires commitment. This program is for shop owners ready to make real changes.
                </p>
                
                <div className="glassmorphic rounded-xl p-6 md:p-10 max-w-2xl mx-auto border-accent">
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold mb-2">Program Terms</h3>
                    <div className="bg-accent/10 text-accent font-semibold px-4 py-2 rounded-lg inline-block mb-4">
                      12-month commitment required
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div className="glassmorphic rounded-lg p-6 border border-white/20">
                        <h4 className="text-xl font-bold mb-2">3-Month Prepay</h4>
                        <div className="text-3xl font-bold mb-1">$5,000</div>
                        <p className="text-white/70 text-sm mb-4">Billed quarterly</p>
                      </div>
                      
                      <div className="glassmorphic rounded-lg p-6 border-2 border-accent relative">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-background px-3 py-1 rounded-full text-sm font-bold">
                          BEST VALUE
                        </div>
                        <h4 className="text-xl font-bold mb-2">12-Month Prepay</h4>
                        <div className="text-3xl font-bold mb-1">$18,000</div>
                        <p className="text-white/70 text-sm mb-4">Save $2,000 vs quarterly</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center mt-8">
                    <motion.a
                      href="https://calendly.com/detailing3/detaileros-mentorship"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary px-8 py-4 text-lg inline-flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Book Fit Check Call</span>
                      <ChevronRight size={20} />
                    </motion.a>
                  </div>
                  
                  <div className="text-center text-sm bg-black/20 p-4 rounded-lg mt-8">
                    <p className="mb-2 text-accent font-semibold">Application Required</p>
                    <p>To ensure you're a good fit for this high-commitment program, all potential mentees go through an application process.</p>
                  </div>
                </div>
              </div>
            </div>
          </SectionTransitionEffect>
        </div>
        
        {/* FAQ Section */}
        <div className="mb-24 max-w-4xl mx-auto">
          <SectionTransitionEffect>
            <div className="text-center mb-10">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Questions Answered
              </motion.span>
              <h2 className="text-3xl font-bold mb-2">Frequently Asked Questions</h2>
            </div>
            
            <div className="space-y-4">
              {[
                {
                  question: "How much time will I need to commit each week?",
                  answer: "Most shop owners spend about 3-4 hours per week on mentorship activities, including our coaching calls, implementation work, and checking in with the community. We design the program to work alongside your busy schedule, not add to your overwhelm."
                },
                {
                  question: "I'm already busy running my shop. How will I find time for this?",
                  answer: "That's exactly why you need this program. We'll help you implement systems that create more time. Many members report gaining back 10+ hours weekly after just the first month of implementing our strategies."
                },
                {
                  question: "Is this only for large shops with multiple employees?",
                  answer: "No. We work with shops of all sizes, from solo operators to multi-location businesses. The principles of systemization and delegation work at every scale, and we'll customize the approach to your specific situation."
                },
                {
                  question: "Do I get direct access to my mentor?",
                  answer: "Absolutely. You'll have scheduled 1-on-1 Zoom sessions plus direct messenger access for quick questions and feedback between sessions. We're committed to being available when you need guidance."
                },
                {
                  question: "Why the 12-month commitment?",
                  answer: "Building lasting systems and transforming your business isn't a quick fix. The 12-month timeframe allows us to implement sustainable changes, test and refine systems, and ensure you have the support needed to navigate the inevitable challenges of business transformation."
                }
              ].map((faq, index) => (
                <motion.div 
                  key={index}
                  className="glassmorphic rounded-xl p-6 hover:border-accent/20 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-white/70">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </SectionTransitionEffect>
        </div>
        
        {/* Call to action */}
        <div className="max-w-3xl mx-auto text-center">
          <SectionTransitionEffect>
            <motion.div 
              className="glassmorphic border-accent rounded-xl p-10 relative overflow-hidden"
              whileHover={{ y: -5, boxShadow: "0 10px 30px -5px rgba(204, 255, 0, 0.2)" }}
              animate={{ 
                boxShadow: ["0 0 15px rgba(204, 255, 0, 0.2)", "0 0 25px rgba(204, 255, 0, 0.4)", "0 0 15px rgba(204, 255, 0, 0.2)"]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <h2 className="text-3xl font-bold mb-6">Ready to reclaim control?</h2>
              <p className="text-xl text-white/80 mb-8">
                Take the first step toward transforming your detailing business into a system that works for you, not the other way around.
              </p>
              
              <motion.a
                href="https://calendly.com/detailing3/detaileros-mentorship"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary px-8 py-4 text-lg inline-flex items-center justify-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Book Fit Check Call</span>
                <ChevronRight size={20} />
              </motion.a>
              
              <p className="mt-10 text-xl font-semibold text-accent">
                Remember: The cars can shine without you. The question is when you decide they will.
              </p>
            </motion.div>
          </SectionTransitionEffect>
        </div>
      </div>
    </div>
  );
};

export default Mentorship;